import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, from } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AuthService } from '../auth/auth.service';
import { environment } from 'src/environments/environment';

export interface PlateRecognitionResult {
    plate: string;
    score: number;
    region: {
        code: string;
        score: number;
    };
    vehicle?: {
        type: string;
        score: number;
    };
    candidates: Array<{
        plate: string;
        score: number;
    }>;
}

export interface BackendPlateRecognitionResponse {
    success: boolean;
    plate: string;
    confidence: number;
    region?: string;
    regionConfidence?: number;
    vehicleType?: string;
    vehicleTypeConfidence?: number;
    candidates: Array<{
        plate: string;
        score: number;
    }>;
    processingTimeMs?: number;
    error?: string;
    message?: string;
}

@Injectable({
    providedIn: 'root'
})
export class PlateRecognizerService {
    private apiUrl = `${environment.cafler_backoffice_back_url}/vehicles`;

    constructor(
        private http: HttpClient,
        private authService: AuthService
    ) { }

    recognizePlateFromFile(imageFile: File, regions?: string[]): Observable<PlateRecognitionResult | null> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    throw new Error('No token available');
                }

                const formData = new FormData();
                formData.append('upload', imageFile);

                const selectedRegions = regions || ['es', 'fr', 'gb', 'de', 'it'];
                selectedRegions.forEach(region => {
                    formData.append('regions', region);
                });

                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${token}`
                });

                return this.http.post<BackendPlateRecognitionResponse>(`${this.apiUrl}/recognize-plate`, formData, { headers }).pipe(
                    map(response => {
                        if (response.success && response.plate) {
                            return {
                                plate: response.plate,
                                score: response.confidence,
                                region: {
                                    code: response.region || 'unknown',
                                    score: response.regionConfidence || 0
                                },
                                vehicle: response.vehicleType ? {
                                    type: response.vehicleType,
                                    score: response.vehicleTypeConfidence || 0
                                } : undefined,
                                candidates: response.candidates || []
                            } as PlateRecognitionResult;
                        }
                        return null;
                    }),
                    catchError(error => {
                        console.error('Error recognizing plate:', error);
                        return throwError(() => error);
                    })
                );
            })
        );
    }

    recognizePlateFromBase64(base64Image: string, regions?: string[]): Observable<PlateRecognitionResult | null> {
        return from(this.authService.getAccessToken()).pipe(
            switchMap((token) => {
                if (!token) {
                    throw new Error('No token available');
                }

                const formData = new FormData();
                formData.append('upload', base64Image);

                const selectedRegions = regions || ['es', 'fr', 'gb', 'de', 'it'];
                selectedRegions.forEach(region => {
                    formData.append('regions', region);
                });

                const headers = new HttpHeaders({
                    'Authorization': `Bearer ${token}`
                });

                return this.http.post<BackendPlateRecognitionResponse>(`${this.apiUrl}/recognize-plate`, formData, { headers }).pipe(
                    map(response => {
                        if (response.success && response.plate) {
                            return {
                                plate: response.plate,
                                score: response.confidence,
                                region: {
                                    code: response.region || 'unknown',
                                    score: response.regionConfidence || 0
                                },
                                vehicle: response.vehicleType ? {
                                    type: response.vehicleType,
                                    score: response.vehicleTypeConfidence || 0
                                } : undefined,
                                candidates: response.candidates || []
                            } as PlateRecognitionResult;
                        }
                        return null;
                    }),
                    catchError(error => {
                        console.error('Error recognizing plate:', error);
                        return throwError(() => error);
                    })
                );
            })
        );
    }
}
